import React from 'react';
import { Card, Row, Col, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaEnvelope, FaPhone, FaPhoneAlt, FaMapMarkerAlt, FaCalendarAlt, FaCalendarCheck, FaBuilding, FaTag, FaCommentAlt, FaArrowLeft } from 'react-icons/fa';
import { CLIENT_STATUSES } from '../../utils/clientModel';

const ClientDetail = ({ client, onDelete }) => {
  // Get status badge color
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case CLIENT_STATUSES.ACTIVE:
        return 'success';
      case CLIENT_STATUSES.INACTIVE:
        return 'secondary';
      case CLIENT_STATUSES.LEAD:
        return 'info';
      case CLIENT_STATUSES.PROSPECT:
        return 'primary';
      case CLIENT_STATUSES.CUSTOMER:
        return 'warning';
      case CLIENT_STATUSES.FORMER:
        return 'danger';
      default:
        return 'light';
    }
  };

  // Format date to DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (!client) {
    return (
      <Card className="client-detail-card shadow-sm">
        <Card.Body className="text-center py-5">
          <div className="empty-state">
            <h3 className="highlight-text">Client not found</h3>
            <p className="text-muted">The client you're looking for doesn't exist or has been deleted.</p>
            <Link to="/" className="btn btn-primary btn-glow">
              <span className="btn-text-wrapper d-flex align-items-center">
                <FaArrowLeft className="me-2" />
                Back to Client List
              </span>
            </Link>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="client-detail-card shadow-sm">
      <Card.Header className="bg-transparent border-0 pt-4 px-4">
        <h4 className="section-title mb-0">Client <span className="highlight-text">Details</span></h4>
      </Card.Header>
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="client-name mb-0">
            {client.firstName} {client.lastName}
            <Badge 
              bg={getStatusBadgeVariant(client.status)} 
              className="status-badge ms-3">
              {client.status}
            </Badge>
          </h2>
          <div>
            <Link to={`/edit/${client.id}`} className="btn btn-outline-primary me-3 btn-action">
              <span className="btn-text-wrapper d-flex align-items-center">
                <FaEdit className="me-2" /> Edit
              </span>
            </Link>
            <Button variant="outline-danger" className="btn-action" onClick={() => onDelete(client.id)}>
              <span className="btn-text-wrapper d-flex align-items-center">
                <FaTrash className="me-2" /> Delete
              </span>
            </Button>
          </div>
        </div>

        <Row className="mb-4">
          <Col md={6}>
            <Card className="detail-card h-100">
              <Card.Body>
                <h5 className="detail-card-title">
                  <span className="detail-icon-wrapper">
                    <FaEnvelope />
                  </span>
                  Contact Information
                </h5>
                <hr className="detail-divider" />
                <div className="detail-item">
                  <div className="detail-label">
                    <FaBuilding className="detail-icon" />
                    Company:
                  </div>
                  <div className="detail-value">{client.company || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">
                    <FaEnvelope className="detail-icon" />
                    Email:
                  </div>
                  <div className="detail-value">
                    {client.email ? (
                      <a href={`mailto:${client.email}`} className="detail-link">{client.email}</a>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">
                    <FaPhone className="detail-icon" />
                    Phone:
                  </div>
                  <div className="detail-value">
                    {client.phone ? (
                      <a href={`tel:${client.phone}`} className="detail-link">{client.phone}</a>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
                {client.alternatePhone && (
                  <div className="detail-item">
                    <div className="detail-label">
                      <FaPhoneAlt className="detail-icon" />
                      Alt Phone:
                    </div>
                    <div className="detail-value">
                      <a href={`tel:${client.alternatePhone}`} className="detail-link">{client.alternatePhone}</a>
                    </div>
                  </div>
                )}
                <div className="detail-item">
                  <div className="detail-label">
                    <FaMapMarkerAlt className="detail-icon" />
                    Address:
                  </div>
                  <div className="detail-value">
                    {client.address || 'N/A'}
                  </div>
                </div>
                <p className="mb-0">
                  <strong>Status:</strong>{' '}
                  <Badge bg={getStatusBadgeVariant(client.status)} pill>
                    {client.status}
                  </Badge>
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="detail-card h-100">
              <Card.Body>
                <h5 className="detail-card-title">
                  <span className="detail-icon-wrapper">
                    <FaCalendarAlt />
                  </span>
                  Additional Information
                </h5>
                <hr className="detail-divider" />
                <div className="detail-item">
                  <div className="detail-label">
                    <FaCalendarAlt className="detail-icon" />
                    First Contact:
                  </div>
                  <div className="detail-value">
                    {client.firstContactDate ? new Date(client.firstContactDate).toLocaleString() : 'N/A'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">
                    <FaCalendarCheck className="detail-icon" />
                    Next Follow-up:
                  </div>
                  <div className="detail-value">
                    {client.nextFollowUp ? new Date(client.nextFollowUp).toLocaleString() : 'N/A'}
                  </div>
                </div>
                {client.tags && (
                  <div className="detail-item">
                    <div className="detail-label">
                      <FaTag className="detail-icon" />
                      Tags:
                    </div>
                    <div className="detail-value">
                      <div className="tags-display">
                        {client.tags.split(',').map((tag, index) => (
                          <Badge key={index} bg="info" className="tag-badge me-1 mb-1">
                            <span className="tag-icon"><FaTag /></span> {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="detail-item">
                  <div className="detail-label">
                    <FaCalendarAlt className="detail-icon" />
                    Created:
                  </div>
                  <div className="detail-value">{formatDate(client.createdAt)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">
                    <FaCalendarAlt className="detail-icon" />
                    Updated:
                  </div>
                  <div className="detail-value">{formatDate(client.updatedAt)}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Body>
            <h5 className="card-title">Notes</h5>
            <hr />
            <p className="mb-0">{client.notes || 'No notes available.'}</p>
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );
};

export default ClientDetail;
